import type { AtomDocument } from '@cpu/domain';

let mermaidTheme: string | null = null;
let started = false;
const katexDone = new WeakSet<Element>();
const mathScheduled = new WeakSet<Element>();

function currentMermaidTheme(): 'default' | 'dark' {
  return document.documentElement.dataset.theme === 'oled' ? 'dark' : 'default';
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function attachCopyButton(pre: HTMLPreElement): void {
  if (pre.dataset.copyReady === 'true') return;
  if (pre.querySelector('code.language-mermaid, code[data-language="mermaid"]')) return;
  if (pre.querySelector('.code-copy-button') || pre.parentElement?.querySelector(':scope > .code-copy-button')) {
    pre.dataset.copyReady = 'true';
    return;
  }
  pre.dataset.copyReady = 'true';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'code-copy-button';
  button.textContent = 'Copy';
  button.setAttribute('aria-label', 'Copy code');
  const copy = async () => {
    const code = pre.querySelector('code')?.textContent ?? pre.textContent ?? '';
    try {
      await navigator.clipboard.writeText(code);
      button.textContent = 'Copied';
    } catch {
      button.textContent = 'Select + copy';
    }
    window.setTimeout(() => { button.textContent = 'Copy'; }, 1200);
  };
  button.addEventListener('click', copy);
  // Inside a .code-window the deployed site positions the copy button over
  // the dots bar (top:6px), so it must be a child of the window, not the pre.
  const host = pre.closest('.code-window') ?? pre;
  host.append(button);
}

function isMermaidPre(pre: HTMLPreElement): boolean {
  return Boolean(pre.querySelector('code.language-mermaid, code[data-language="mermaid"]'));
}

function convertMermaid(pre: HTMLPreElement): HTMLElement | null {
  const code = pre.querySelector('code.language-mermaid, code[data-language="mermaid"]');
  if (!code) return null;
  if (pre.dataset.mermaidConverted === 'true') return null;
  pre.dataset.mermaidConverted = 'true';
  const container = document.createElement('div');
  container.className = 'mermaid mermaid-diagram';
  container.dataset.sourceId = code.textContent ?? '';
  container.textContent = code.textContent ?? '';
  pre.replaceWith(container);
  return container;
}

async function renderMermaid(nodes: HTMLElement[]): Promise<void> {
  if (!nodes.length) return;
  const { default: mermaid } = await import('mermaid');
  const theme = currentMermaidTheme();
  if (theme !== mermaidTheme) {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      theme: 'base',
      themeVariables: theme === 'dark'
        ? {
            background: '#0a0a0a',
            primaryColor: '#101a2d',
            primaryTextColor: '#e9e8e2',
            primaryBorderColor: '#82aaff',
            secondaryColor: '#29130f',
            tertiaryColor: '#0d211a',
            lineColor: '#8b8a85',
            fontFamily: 'Instrument Sans, sans-serif'
          }
        : {
            background: '#fbfaf5',
            primaryColor: '#e8edff',
            primaryTextColor: '#181816',
            primaryBorderColor: '#2854c5',
            secondaryColor: '#fff0eb',
            tertiaryColor: '#e3f4ed',
            lineColor: '#77766d',
            fontFamily: 'Instrument Sans, sans-serif'
          }
    });
    mermaidTheme = theme;
  }
  try {
    await mermaid.run({ nodes });
  } catch {
    for (const container of nodes) {
      if (!container.querySelector('svg')) container.classList.add('mermaid-failed');
    }
  }
}

async function renderMath(root: Element): Promise<void> {
  if (katexDone.has(root) || mathScheduled.has(root)) return;
  const text = root.textContent ?? '';
  if (!/\$[^$]|\\\(|\\\[/.test(text)) return;
  mathScheduled.add(root);
  try {
    const { default: renderMathInElement } = await import('katex/contrib/auto-render');
    renderMathInElement(root as HTMLElement, {
      delimiters: [
        { left: '$$', right: '$$', display: true },
        { left: '\\[', right: '\\]', display: true },
        { left: '\\(', right: '\\)', display: false },
        { left: '$', right: '$', display: false }
      ],
      ignoredTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code'],
      throwOnError: false
    });
    katexDone.add(root);
  } finally {
    mathScheduled.delete(root);
  }
}

function processNode(node: Node): void {
  if (!(node instanceof HTMLElement)) return;
  const pres = node.tagName === 'PRE'
    ? [node as HTMLPreElement]
    : [...node.querySelectorAll<HTMLPreElement>('pre')];
  const mermaidNodes: HTMLElement[] = [];
  for (const pre of pres) {
    const container = convertMermaid(pre);
    if (container) {
      mermaidNodes.push(container);
      continue;
    }
    if (pre.isConnected) attachCopyButton(pre);
  }
  if (mermaidNodes.length) void renderMermaid(mermaidNodes);
  void renderMath(node);
}

/** Re-render all existing mermaid diagrams (theme flip). */
export function rerenderMermaidDiagrams(): void {
  const nodes = [...document.querySelectorAll<HTMLElement>('div.mermaid-diagram[data-source-id]')];
  for (const container of nodes) {
    if (container.querySelector('svg')) container.textContent = container.dataset.sourceId ?? '';
  }
  void renderMermaid(nodes);
}

/**
 * Observe the document and enhance content as it appears: code copy buttons,
 * mermaid diagrams and KaTeX math. Immune to React render/commit ordering.
 */
export function startContentEnhancer(): void {
  if (started || typeof document === 'undefined') return;
  started = true;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) processNode(node);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Initial pass for server-rendered or pre-existing content.
  for (const pre of document.querySelectorAll<HTMLPreElement>('pre')) {
    const container = convertMermaid(pre);
    if (container) void renderMermaid([container]);
    else attachCopyButton(pre);
  }
  void renderMath(document.body);

  // Re-render diagrams when the theme flips.
  new MutationObserver(() => rerenderMermaidDiagrams()).observe(
    document.documentElement,
    { attributes: true, attributeFilter: ['data-theme'] }
  );
}

export type { AtomDocument };
